import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import EquipoSelect from "@/components/EquipoSelect";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Save, Loader2, Camera, Send, X, Plus, Trash2, CheckCircle, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import ConsumoRepuestos from "@/components/ConsumoRepuestos";
import RepuestoCombobox from "@/components/RepuestoCombobox";

import { logoColor as LOGO_URL } from "@/lib/logos";
const STEPS = ["Datos y equipo", "Intervención", "Verificación", "Recepción y cierre"];

const TIPO_EQUIPO = [
  { value: "unidad_dental", label: "UNIDAD DENTAL" },
  { value: "compresor", label: "COMPRESOR" },
  { value: "instrumental_rotatorio", label: "INSTRUMENTAL ROTATORIO" },
  { value: "escaler", label: "ESCALER" },
  { value: "lampara_fotocurado", label: "LÁMPARA FOTOCURADO" },
  { value: "red_agua", label: "RED DE AGUA" },
  { value: "red_aire", label: "RED DE AIRE" },
  { value: "red_electrica", label: "RED ELÉCTRICA" },
  { value: "otros", label: "OTROS" },
];
const TIPO_FALLA = [
  { value: "electrica", label: "Eléctrica" },
  { value: "mecanica", label: "Mecánica" },
  { value: "neumatica", label: "Neumática" },
  { value: "hidraulica", label: "Hidráulica" },
  { value: "software", label: "Software" },
  { value: "desgaste", label: "Desgaste natural" },
  { value: "otro", label: "Otro" },
];
const ESTADO_FINAL = [
  { value: "operativo", label: "Operativo" },
  { value: "operativo_con_observaciones", label: "Operativo con observaciones" },
  { value: "no_operativo", label: "No operativo" },
];
const TIPO_ASISTENCIA = [
  { value: "plan_centro_fijo", label: "PLAN CENTRO FIJO" },
  { value: "correctiva_simple_rm", label: "CORRECTIVA SIMPLE RM" },
  { value: "correctiva_compleja_rm", label: "CORRECTIVA COMPLEJA RM" },
  { value: "correctiva_simple_fh_rm", label: "CORRECTIVA SIMPLE FUERA HORARIO RM" },
  { value: "correctiva_compleja_fh_rm", label: "CORRECTIVA COMPLEJA FUERA HORARIO RM" },
  { value: "correctiva_simple_regiones", label: "CORRECTIVA SIMPLE REGIONES" },
  { value: "correctiva_compleja_regiones", label: "CORRECTIVA COMPLEJA REGIONES" },
  { value: "correctiva_simple_fh_regiones", label: "CORRECTIVA SIMPLE FUERA HORARIO REGIONES" },
  { value: "correctiva_compleja_fh_regiones", label: "CORRECTIVA COMPLEJA FUERA HORARIO REGIONES" },
  { value: "desinstalacion_retiro_ud", label: "DESINSTALACIÓN + RETIRO UNIDAD DENTAL" },
  { value: "desinstalacion_ud", label: "DESINSTALACIÓN UNIDAD DENTAL" },
  { value: "instalacion_ud", label: "INSTALACIÓN UNIDAD DENTAL" },
  { value: "retiro_ud", label: "RETIRO UNIDAD DENTAL" },
  { value: "flete", label: "FLETE" },
  { value: "solicitud_repuestos", label: "SOLICITUD DE REPUESTOS" },
  { value: "instalacion_otro", label: "INSTALACIÓN OTRO EQUIPO" },
  { value: "retiro_otro", label: "RETIRO OTRO EQUIPO" },
  { value: "desinstalacion_otro", label: "DESINSTALACIÓN OTRO EQUIPO" },
  { value: "otro", label: "OTRO" },
];

export default function MantencionCorrectiva() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user, isAdmin } = useAuth();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reportId, setReportId] = useState(id || null);
  const [folio, setFolio] = useState("");
  const [estado, setEstado] = useState("borrador");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSnapshotRef = useRef("");
  const stateRef = useRef({ datos: null, equipoMC: null, intervencion: null, recepcion: null, verificacion: null });
  const reportIdRef = useRef(reportId);
  const inFlightSaveRef = useRef(null);  // serializa los PUT
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);

  const [clients, setClients] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [datos, setDatos] = useState({
    fecha: new Date().toISOString().split("T")[0], hora_inicio: new Date().toTimeString().slice(0, 5),
    hora_termino: "", cliente_id: "", cliente: "", sucursal: "", direccion: "",
    contacto_cliente: "", correo_cliente: "", telefono_cliente: "",
    marca_sillon: "", modelo: "", numero_serie: "", numero_box: "", codigo_interno: "", estado_inicial: "",
  });
  const [equipoMC, setEquipoMC] = useState({ tipo_equipo: "", tipo_equipo_otro: "", equipo_operativo: "" });
  const [intervencion, setIntervencion] = useState({
    diagnostico: "", tipo_falla: "", tipo_falla_otro: "",
    repuestos_usados: "no", repuestos_utilizados: [],
    estado_final: "", repuestos_necesarios: "no", repuesto_solicitar: "",
    observaciones_adicionales: "", causa_raiz: "", imagenes: [],
  });
  const [verificacion, setVerificacion] = useState([]);
  const [recepcion, setRecepcion] = useState({
    nombre_recepcion: "", correo_envio: "", fecha_termino: new Date().toISOString().split("T")[0],
    tipo_asistencia: "", tipo_asistencia_otro: "",
  });

  useEffect(() => {
    api.get("/api/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/api/repuestos").then((r) => setCatalogo(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (id) {
      api.get(`/api/maintenance/${id}`).then((r) => {
        const d = r.data;
        const loadedDatos = d.datos_generales || {};
        const loadedEquipo = d.datos_equipo_mc || { tipo_equipo: "", tipo_equipo_otro: "", equipo_operativo: "" };
        const loadedInter = d.intervencion || { diagnostico: "", tipo_falla: "", tipo_falla_otro: "", repuestos_usados: "no", repuestos_utilizados: [], estado_final: "", repuestos_necesarios: "no", repuesto_solicitar: "", observaciones_adicionales: "", causa_raiz: "", imagenes: [] };
        const loadedVerif = d.verificacion_post || [];
        const loadedRec = d.recepcion || { nombre_recepcion: "", correo_envio: "", fecha_termino: "", tipo_asistencia: "", tipo_asistencia_otro: "" };
        setDatos(loadedDatos);
        setEquipoMC(loadedEquipo);
        setIntervencion(loadedInter);
        setVerificacion(loadedVerif);
        setRecepcion(loadedRec);
        setFolio(d.folio); setReportId(d.id);
        setEstado(d.estado || "borrador");
        // Sembrar snapshot para evitar PUT espurio inicial
        lastSnapshotRef.current = JSON.stringify({
          datos: loadedDatos, equipoMC: loadedEquipo,
          intervencion: loadedInter, recepcion: loadedRec, verificacion: loadedVerif,
        });
        if (d.estado === "completado" && user?.cargo !== "admin") setReadOnly(true);
      }).catch(() => toast.error("Error al cargar reporte"));
    } else {
      api.get("/api/maintenance/template/mc").then((r) => {
        setVerificacion(r.data.map((item) => ({ ...item, resultado: "", observacion: "" })));
      }).catch(() => {});
    }
  }, [id, api]);

  const ud = (f, v) => setDatos((p) => ({ ...p, [f]: v }));
  const ue = (f, v) => setEquipoMC((p) => ({ ...p, [f]: v }));
  const ui = (f, v) => setIntervencion((p) => ({ ...p, [f]: v }));
  const ur = (f, v) => setRecepcion((p) => ({ ...p, [f]: v }));
  const setVerifItem = (i, f, v) => { const n = [...verificacion]; n[i] = { ...n[i], [f]: v }; setVerificacion(n); };

  const handleClientSelect = (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    ud("cliente_id", clientId); ud("cliente", c.nombre);
    ud("contacto_cliente", c.contacto_nombre || ""); ud("correo_cliente", c.contacto_email || "");
    ud("telefono_cliente", c.contacto_telefono || ""); ud("sucursal", ""); ud("direccion", "");
    ur("correo_envio", c.contacto_email || "");
    // Reset destinatarios when client changes
    buildDestinatarios(c.contacto_email || "", "", user?.email || "");
  };
  const handleSucursalSelect = (v) => {
    const d = selectedClient?.direcciones?.find((x) => x.nombre === v);
    ud("sucursal", v); ud("direccion", d?.direccion || "");
    buildDestinatarios(datos.correo_cliente, d?.correo || "", user?.email || "");
  };
  const buildDestinatarios = (correoCliente, correoSucursal, correoTecnico) => {
    const dest = [];
    if (correoCliente && correoCliente.includes("@")) dest.push({ email: correoCliente, origen: "Cliente", removido: false });
    if (correoSucursal && correoSucursal.includes("@")) dest.push({ email: correoSucursal, origen: "Sucursal", removido: false });
    if (correoTecnico && correoTecnico.includes("@")) dest.push({ email: correoTecnico, origen: "Técnico", removido: false });
    ur("destinatarios", dest);
  };
  const selectedClient = clients.find((c) => c.id === datos.cliente_id);

  // Repuestos from catalog
  const addRepuestoCatalogoRow = () =>
    ui("repuestos_utilizados", [...intervencion.repuestos_utilizados, { sku: "", nombre: "", cantidad: 1, es_otro: false }]);
  const updateRepuestoCatalogo = (i, repId) => {
    const rep = catalogo.find((r) => r.id === repId);
    if (!rep) return;
    const n = [...intervencion.repuestos_utilizados];
    n[i] = { ...n[i], sku: rep.sku, nombre: rep.nombre, es_otro: false };
    ui("repuestos_utilizados", n);
  };
  const addRepuestoOtro = () => {
    const ok = window.confirm(
      "¿Estás seguro que el repuesto no se encuentra en el catálogo?\n\n" +
      "Aceptar: agregar repuesto como texto libre.\n" +
      "Cancelar: volver a revisar el catálogo."
    );
    if (!ok) return;
    ui("repuestos_utilizados", [...intervencion.repuestos_utilizados, { sku: "OTROS", nombre: "", cantidad: 1, es_otro: true }]);
  };
  const updateRepuesto = (i, f, v) => { const n = [...intervencion.repuestos_utilizados]; n[i] = { ...n[i], [f]: v }; ui("repuestos_utilizados", n); };
  const removeRepuesto = (i) => ui("repuestos_utilizados", intervencion.repuestos_utilizados.filter((_, idx) => idx !== i));

  // Images with comments
  const MAX_IMAGES = 6;
  const handleFileChange = async (e) => {
    // Admite varias fotos de una vez (galería con selección múltiple).
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const actuales = intervencion.imagenes || [];
    const cupo = MAX_IMAGES - actuales.length;
    if (cupo <= 0) {
      toast.error(`Límite alcanzado: solo se permiten ${MAX_IMAGES} fotos por reporte.`);
      return;
    }
    const validas = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: solo imágenes`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: máximo 10MB`); continue; }
      validas.push(f);
    }
    if (validas.length === 0) return;
    if (validas.length > cupo) toast.info(`Solo se subirán ${cupo} (máximo ${MAX_IMAGES} fotos por reporte).`);
    const url = reportId
      ? `/api/maintenance/images/upload?report_id=${reportId}`
      : "/api/maintenance/images/upload";
    const nuevas = [];
    for (const f of validas.slice(0, cupo)) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
        nuevas.push({ id: r.data.id, comentario: "" });
      } catch (err) { toast.error(err.response?.data?.detail || `Error al subir ${f.name}`); }
    }
    if (nuevas.length) {
      ui("imagenes", [...actuales, ...nuevas]);
      toast.success(nuevas.length === 1 ? "Imagen subida" : `${nuevas.length} imágenes subidas`);
    }
  };
  const deleteImage = async (img) => {
    const imgId = typeof img === "string" ? img : img.id;
    try { await api.delete(`/api/maintenance/images/${imgId}`);
      ui("imagenes", intervencion.imagenes.filter((i) => (typeof i === "string" ? i : i.id) !== imgId));
      toast.success("Imagen eliminada");
    } catch { toast.error("Error al eliminar"); }
  };
  const updateImageComment = (idx, comentario) => {
    const imgs = [...intervencion.imagenes];
    imgs[idx] = typeof imgs[idx] === "string" ? { id: imgs[idx], comentario } : { ...imgs[idx], comentario };
    ui("imagenes", imgs);
  };
  const getImgId = (img) => typeof img === "string" ? img : img.id;

  // Save & Complete
  const saveDraft = async ({ silent = false } = {}) => {
    if (readOnly) return reportId;
    if (inFlightSaveRef.current) {
      try { await inFlightSaveRef.current; } catch (_) {}
    }
    const p = (async () => {
      setSaving(true);
      try {
        if (!reportId) {
          const r = await api.post("/api/maintenance", { tipo: "correctiva", datos_generales: datos });
          setReportId(r.data.id); setFolio(r.data.folio);
          await api.put(`/api/maintenance/${r.data.id}`, { datos_generales: datos, datos_equipo_mc: equipoMC, intervencion, recepcion, verificacion_post: verificacion });
          if (!silent) toast.success(`Borrador creado: ${r.data.folio}`);
          setLastSavedAt(Date.now());
          lastSnapshotRef.current = JSON.stringify({ datos, equipoMC, intervencion, recepcion, verificacion });
          return r.data.id;
        }
        await api.put(`/api/maintenance/${reportId}`, { datos_generales: datos, datos_equipo_mc: equipoMC, intervencion, recepcion, verificacion_post: verificacion });
        if (!silent) toast.success("Borrador guardado");
        setLastSavedAt(Date.now());
        lastSnapshotRef.current = JSON.stringify({ datos, equipoMC, intervencion, recepcion, verificacion });
        return reportId;
      } catch {
        if (!silent) toast.error("Error al guardar");
        return null;
      } finally { setSaving(false); }
    })();
    inFlightSaveRef.current = p;
    try { return await p; }
    finally { if (inFlightSaveRef.current === p) inFlightSaveRef.current = null; }
  };

  // Auto-guardado del borrador (mismo patrón que MP).
  useEffect(() => {
    stateRef.current = { datos, equipoMC, intervencion, recepcion, verificacion };
  }, [datos, equipoMC, intervencion, recepcion, verificacion]);

  useEffect(() => {
    if (readOnly || saving || completing) return;
    if (!reportId && !datos.cliente_id) return;
    const snap = JSON.stringify({ datos, equipoMC, intervencion, recepcion, verificacion });
    if (snap === lastSnapshotRef.current) return;
    const t = setTimeout(() => { saveDraft({ silent: true }); }, 2000);
    return () => clearTimeout(t);
  }, [datos, equipoMC, intervencion, recepcion, verificacion, reportId, readOnly, saving, completing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush al cerrar la pestaña.
  useEffect(() => {
    const onBeforeUnload = () => {
      const rid = reportIdRef.current;
      const s = stateRef.current;
      if (!rid || !s.datos) return;
      const snap = JSON.stringify(s);
      if (snap === lastSnapshotRef.current) return;
      const token = localStorage.getItem("odf_token");
      const url = `${api.defaults.baseURL || ""}/api/maintenance/${rid}`;
      try {
        fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json",
                     ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            datos_generales: s.datos, datos_equipo_mc: s.equipoMC,
            intervencion: s.intervencion, recepcion: s.recepcion,
            verificacion_post: s.verificacion,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* best effort */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [api]);

  // Validation
  const verifComplete = verificacion.length > 0 && verificacion.every((v) => v.resultado);
  const hasPhotos = intervencion.imagenes.length > 0;
  // La foto obligatoria no aplica para el admin (puede editar/regenerar sin foto)
  const fotosOk = hasPhotos || isAdmin;
  // Condicionales "Especificar otros": si el desplegable es "otros" hay que
  // tener el texto libre asociado.
  const tipoEquipoOk = equipoMC.tipo_equipo && (
    equipoMC.tipo_equipo !== "otros" || (equipoMC.tipo_equipo_otro || "").trim()
  );
  const tipoFallaOk = intervencion.tipo_falla && (
    intervencion.tipo_falla !== "otros" || (intervencion.tipo_falla_otro || "").trim()
  );
  const tipoAsistenciaOk = recepcion.tipo_asistencia && (
    recepcion.tipo_asistencia !== "otros" || (recepcion.tipo_asistencia_otro || "").trim()
  );
  const canComplete = datos.cliente && datos.sucursal && tipoEquipoOk && equipoMC.equipo_operativo &&
    datos.marca_sillon && datos.modelo && datos.numero_box && tipoFallaOk && intervencion.diagnostico &&
    intervencion.estado_final && verifComplete && recepcion.nombre_recepcion && tipoAsistenciaOk &&
    recepcion.fecha_termino && fotosOk;

  const completeReport = async () => {
    if (!canComplete) { toast.error("Completa todos los campos obligatorios"); return; }
    setCompleting(true);
    try {
      // Espera cualquier auto-save en vuelo para evitar que sobreescriba el PUT final.
      if (inFlightSaveRef.current) {
        try { await inFlightSaveRef.current; } catch (_) {}
      }
      if (reportId) await api.put(`/api/maintenance/${reportId}`, { datos_generales: datos, datos_equipo_mc: equipoMC, intervencion, recepcion, verificacion_post: verificacion });
      lastSnapshotRef.current = JSON.stringify({ datos, equipoMC, intervencion, recepcion, verificacion });
      const r = await api.post(`/api/maintenance/${reportId}/complete`);
      const isResend = r.data.reenvio;
      toast.success(isResend
        ? `Reporte ${r.data.folio} actualizado y reenviado`
        : `Reporte ${r.data.folio} completado`);
      if (r.data.correo_enviado) toast.success("Correo enviado al cliente");
      else if (r.data.email_error) toast.error(`Correo no enviado: ${r.data.email_error}`);
      navigate("/tecnico/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al completar"); } finally { setCompleting(false); }
  };

  const Req = () => <span className="text-red-500 text-xs">*</span>;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091]">{folio || "Nueva MC"}</span>
            <Badge className="bg-[#384091] text-white text-[10px]">Correctiva</Badge>
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <span className="hidden sm:inline-block text-[11px] text-gray-500 mr-1">
                {saving
                  ? "Guardando…"
                  : lastSavedAt
                    ? `Guardado ${new Date(lastSavedAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`
                    : "Auto-guardado activo"}
              </span>
            )}
            {!readOnly && <Button variant="outline" size="sm" onClick={() => saveDraft()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1 hidden sm:inline">Guardar</span>
            </Button>}
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
              <ArrowLeft className="h-4 w-4" /><span className="ml-1">Volver</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4">
        {readOnly && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700 font-medium">
            🔒 Reporte completado — Solo lectura. Un administrador puede editarlo.
          </div>
        )}
        {!readOnly && estado === "completado" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-700 font-medium">
            ✏️ Editando reporte completado. Al guardar y enviar se regenerará el PDF y se reenviará el correo.
          </div>
        )}
        <div className="flex gap-1 mb-4">
          {STEPS.map((s, i) => (
            <button key={i} onClick={() => !readOnly && setStep(i)}
              className={`flex-1 py-2 px-2 text-xs font-medium rounded-lg transition ${i === step ? "bg-[#384091] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
              {i + 1}. {s}
            </button>
          ))}
        </div>

        {/* STEP 1: Datos y equipo */}
        {step === 0 && (
          <Card><CardContent className="p-4 space-y-4">
            <h3 className="text-lg font-semibold text-[#384091]">Datos clínica y equipo</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Cliente <Req /></Label>
                <Select value={datos.cliente_id} onValueChange={handleClientSelect} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                  <SelectContent className="bg-white">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select></div>
              {selectedClient?.direcciones?.length > 0 && (
                <div className="sm:col-span-2"><Label>Sucursal <Req /></Label>
                  <Select value={datos.sucursal} onValueChange={handleSucursalSelect} disabled={readOnly}>
                    <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="Seleccionar sucursal" /></SelectTrigger>
                    <SelectContent className="bg-white">{selectedClient.direcciones.map((d, i) => <SelectItem key={i} value={d.nombre}>{d.nombre} — {d.direccion}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
              <div><Label>Fecha</Label><Input type="date" value={datos.fecha} onChange={(e) => ud("fecha", e.target.value)} className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
            </div>
            <h4 className="text-md font-semibold text-[#0FB4B7] pt-2">Datos del equipo</h4>
            {!readOnly && isAdmin && datos.cliente_id && (
              <EquipoSelect
                clienteId={datos.cliente_id}
                sucursal={datos.sucursal}
                onSelect={(eq) => {
                  setDatos((p) => ({
                    ...p,
                    marca_sillon: eq.marca || p.marca_sillon,
                    modelo: eq.modelo || p.modelo,
                    numero_serie: eq.numero_serie || p.numero_serie,
                    numero_box: eq.numero_box || p.numero_box,
                    codigo_interno: eq.codigo_interno || p.codigo_interno,
                  }));
                  if (eq.tipo_equipo) setEquipoMC((p) => ({ ...p, tipo_equipo: eq.tipo_equipo }));
                }}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Tipo equipo <Req /></Label>
                <Select value={equipoMC.tipo_equipo} onValueChange={(v) => ue("tipo_equipo", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className="bg-white">{TIPO_EQUIPO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              {equipoMC.tipo_equipo === "otros" && (
                <div><Label>Especificar equipo <Req /></Label><Input value={equipoMC.tipo_equipo_otro || ""} onChange={(e) => ue("tipo_equipo_otro", e.target.value)} placeholder="Tipo de equipo..." className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              )}
              <div><Label>Equipo operativo al llegar <Req /></Label>
                <Select value={equipoMC.equipo_operativo} onValueChange={(v) => ue("equipo_operativo", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="SI / NO" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="si">SI</SelectItem><SelectItem value="no">NO</SelectItem></SelectContent>
                </Select></div>
              <div><Label>Marca <Req /></Label><Input value={datos.marca_sillon} onChange={(e) => ud("marca_sillon", e.target.value)} className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              <div><Label>Modelo <Req /></Label><Input value={datos.modelo} onChange={(e) => ud("modelo", e.target.value)} className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              <div><Label>N° Box <Req /></Label><Input type="number" min="1" inputMode="numeric" value={datos.numero_box} onChange={(e) => ud("numero_box", e.target.value.replace(/\D/g, ""))} className="mt-1 h-11 bg-white" disabled={readOnly} placeholder="Solo números" /></div>
              <div><Label>N° Serie</Label><Input value={datos.numero_serie} onChange={(e) => ud("numero_serie", e.target.value)} className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              <div><Label>Código interno</Label><Input value={datos.codigo_interno} onChange={(e) => ud("codigo_interno", e.target.value)} className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
            </div>
          </CardContent></Card>
        )}

        {/* STEP 2: Intervención técnica */}
        {step === 1 && (
          <Card><CardContent className="p-4 space-y-4">
            <h3 className="text-lg font-semibold text-[#0FB4B7]">Intervención técnica</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Tipo de falla <Req /></Label>
                <Select value={intervencion.tipo_falla} onValueChange={(v) => ui("tipo_falla", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className="bg-white">{TIPO_FALLA.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              {intervencion.tipo_falla === "otro" && (
                <div><Label>Especificar falla <Req /></Label><Input value={intervencion.tipo_falla_otro} onChange={(e) => ui("tipo_falla_otro", e.target.value)} placeholder="Tipo de falla..." className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              )}
              <div><Label>Estado final equipo <Req /></Label>
                <Select value={intervencion.estado_final} onValueChange={(v) => ui("estado_final", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className="bg-white">{ESTADO_FINAL.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label>Diagnóstico <Req /></Label>
              <Textarea value={intervencion.diagnostico} onChange={(e) => ui("diagnostico", e.target.value)} placeholder="Descripción del problema y trabajo realizado..." className="mt-1 bg-white min-h-[100px]" disabled={readOnly} /></div>

            {/* Repuestos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>¿Se usaron repuestos?</Label>
                <Select value={intervencion.repuestos_usados} onValueChange={(v) => ui("repuestos_usados", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="no">NO</SelectItem><SelectItem value="si">SI</SelectItem></SelectContent>
                </Select></div>
            </div>
            {intervencion.repuestos_usados === "si" && (
              <div className="border rounded-lg p-3 space-y-3 bg-gray-50/40">
                <h4 className="text-sm font-semibold text-[#384091]">Repuestos utilizados</h4>

                {/* Catálogo */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Del catálogo</Label>
                  {intervencion.repuestos_utilizados.filter((r) => !r.es_otro).length === 0 && (
                    <p className="text-xs text-gray-400">No se han agregado repuestos del catálogo.</p>
                  )}
                  {intervencion.repuestos_utilizados.map((rep, i) => {
                    if (rep.es_otro) return null;
                    return (
                      <div key={i} className="flex gap-2 items-center">
                        <RepuestoCombobox
                          catalogo={catalogo}
                          value={catalogo.find((c) => c.sku === rep.sku)?.id || ""}
                          onSelect={(repId) => updateRepuestoCatalogo(i, repId)}
                          disabled={readOnly}
                          placeholder={rep.nombre || "Buscar repuesto..."}
                        />
                        <Input type="number" min="1" inputMode="numeric" value={rep.cantidad} onChange={(e) => updateRepuesto(i, "cantidad", parseInt(e.target.value) || 1)} className="w-16 bg-white text-center h-11" disabled={readOnly} />
                        {!readOnly && <Button variant="ghost" size="sm" onClick={() => removeRepuesto(i)} className="h-11 w-11 p-0"><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <Button variant="outline" size="sm" onClick={addRepuestoCatalogoRow} className="border-dashed border-[#0FB4B7]/40 text-[#0FB4B7] hover:bg-[#0FB4B7]/5">
                      <Plus className="h-4 w-4 mr-1" /> Agregar repuesto del catálogo
                    </Button>
                  )}
                </div>

                {/* Otros */}
                <div className="space-y-2 pt-2">
                  <Label className="text-xs text-gray-500">Otros repuestos (texto libre)</Label>
                  {intervencion.repuestos_utilizados.map((rep, i) => {
                    if (!rep.es_otro) return null;
                    return (
                      <div key={i} className="flex gap-2 items-center">
                        <Input value={rep.nombre} onChange={(e) => updateRepuesto(i, "nombre", e.target.value)} placeholder="Nombre del repuesto..." className="flex-1 bg-white h-11" disabled={readOnly} />
                        <Input type="number" min="1" inputMode="numeric" value={rep.cantidad} onChange={(e) => updateRepuesto(i, "cantidad", parseInt(e.target.value) || 1)} className="w-16 bg-white text-center h-11" disabled={readOnly} />
                        {!readOnly && <Button variant="ghost" size="sm" onClick={() => removeRepuesto(i)} className="h-11 w-11 p-0"><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <Button variant="outline" size="sm" onClick={addRepuestoOtro} className="border-dashed border-[#384091]/40 text-[#384091] hover:bg-[#384091]/5">
                      <Plus className="h-4 w-4 mr-1" /> Otros repuestos
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>¿Se necesitan repuestos?</Label>
                <Select value={intervencion.repuestos_necesarios} onValueChange={(v) => ui("repuestos_necesarios", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="no">NO</SelectItem><SelectItem value="si">SI</SelectItem></SelectContent>
                </Select></div>
            </div>
            {intervencion.repuestos_necesarios === "si" && (
              <div><Label>Repuestos a solicitar</Label><Textarea value={intervencion.repuesto_solicitar} onChange={(e) => ui("repuesto_solicitar", e.target.value)} placeholder="Detalle..." className="mt-1 bg-white" disabled={readOnly} /></div>
            )}
            <div><Label>Observaciones adicionales</Label><Textarea value={intervencion.observaciones_adicionales} onChange={(e) => ui("observaciones_adicionales", e.target.value)} placeholder="Notas..." className="mt-1 bg-white" disabled={readOnly} /></div>
            <div><Label>Causa raíz (opcional)</Label><Textarea value={intervencion.causa_raiz} onChange={(e) => ui("causa_raiz", e.target.value)} placeholder="¿Por qué falló?" className="mt-1 bg-white" disabled={readOnly} /></div>

            {/* Images */}
            <div>
              <Label>Registro fotográfico <Req /> <span className="text-xs text-gray-400 font-normal">(mín. 1, máx. {MAX_IMAGES}) — {intervencion.imagenes.length}/{MAX_IMAGES}</span></Label>
              {!hasPhotos && !isAdmin && <p className="text-xs text-red-500 mt-1"><AlertTriangle className="h-3 w-3 inline mr-1" />Debes adjuntar al menos 1 fotografía</p>}
              <div className="space-y-3 mt-2">
                {intervencion.imagenes.map((img, idx) => {
                  const imgId = getImgId(img);
                  const comentario = typeof img === "string" ? "" : (img.comentario || "");
                  return (
                    <div key={imgId} className="flex gap-3 items-start bg-gray-50 rounded-lg p-2">
                      <div className="relative w-24 h-24 rounded border overflow-hidden flex-shrink-0">
                        <img src={`${api.defaults.baseURL}/api/maintenance/images/${imgId}`} alt="" className="w-full h-full object-cover" />
                        {!readOnly && <button onClick={() => deleteImage(img)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"><X className="h-3 w-3" /></button>}
                      </div>
                      <div className="flex-1">
                        <Input value={comentario} onChange={(e) => updateImageComment(idx, e.target.value)} placeholder="Comentario de la imagen..." className="bg-white h-11" disabled={readOnly} />
                        <p className="text-[10px] text-gray-400 mt-1">Imagen {idx + 1}</p>
                      </div>
                    </div>);
                })}
              </div>
              {!readOnly && intervencion.imagenes.length < MAX_IMAGES && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button onClick={() => cameraRef.current?.click()} className="py-3 rounded-lg border-2 border-dashed border-[#0FB4B7]/40 flex items-center justify-center gap-2 text-[#0FB4B7] hover:bg-[#0FB4B7]/5">
                    <Camera className="h-4 w-4" /><span className="text-sm font-medium">Tomar foto</span>
                  </button>
                  <button onClick={() => galleryRef.current?.click()} className="py-3 rounded-lg border-2 border-dashed border-[#384091]/40 flex items-center justify-center gap-2 text-[#384091] hover:bg-[#384091]/5">
                    <ImageIcon className="h-4 w-4" /><span className="text-sm font-medium">Cargar imagen</span>
                  </button>
                </div>
              )}
              {!readOnly && intervencion.imagenes.length >= MAX_IMAGES && (
                <p className="mt-2 text-xs text-gray-500 text-center">Has alcanzado el límite de {MAX_IMAGES} fotos.</p>
              )}
            </div>
          </CardContent></Card>
        )}

        {/* STEP 3: Verificación post-reparación */}
        {step === 2 && (
          <Card><CardContent className="p-4 space-y-4">
            <h3 className="text-lg font-semibold text-[#384091]">Verificación post-reparación</h3>
            <p className="text-sm text-gray-500">Todos los ítems son obligatorios. Marque el estado de cada verificación.</p>
            {!verifComplete && <p className="text-xs text-red-500"><AlertTriangle className="h-3 w-3 inline mr-1" />Todos los ítems deben tener un estado seleccionado</p>}
            <div className="space-y-2">
              {verificacion.map((item, i) => (
                <div key={item.id} className={`rounded-lg border p-3 ${item.resultado === "no_conforme" ? "border-red-200 bg-red-50" : item.resultado ? "border-green-200 bg-green-50/30" : "border-gray-200"}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-bold text-gray-400 mt-1 w-5">{i + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.descripcion}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[["conforme", "Conforme", "bg-green-100 text-green-700 border-green-300"],
                          ["no_conforme", "No conforme", "bg-red-100 text-red-700 border-red-300"],
                          ["no_aplica", "No Aplica", "bg-gray-100 text-gray-600 border-gray-300"]
                        ].map(([val, lbl, cls]) => (
                          <button key={val} onClick={() => !readOnly && setVerifItem(i, "resultado", val)}
                            className={`px-3 py-1 text-xs rounded-full border transition ${item.resultado === val ? cls + " font-bold" : "border-gray-200 text-gray-400 hover:bg-gray-50"}`}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                      <Input value={item.observacion || ""} onChange={(e) => setVerifItem(i, "observacion", e.target.value)}
                        placeholder="Observación (opcional)..." className="mt-2 h-11 bg-white" disabled={readOnly} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <span className="font-medium">{verificacion.filter((v) => v.resultado).length}/{verificacion.length}</span> ítems completados
              {verifComplete && <CheckCircle className="h-4 w-4 text-green-500 inline ml-2" />}
            </div>
          </CardContent></Card>
        )}

        {/* STEP 4: Recepción y cierre */}
        {step === 3 && (
          <Card><CardContent className="p-4 space-y-4">
            <h3 className="text-lg font-semibold text-[#384091]">Recepción conforme</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nombre quien recibe <Req /></Label>
                <Input value={recepcion.nombre_recepcion} onChange={(e) => ur("nombre_recepcion", e.target.value)} className="mt-1 h-11 bg-white" autoComplete="name" disabled={readOnly} /></div>
              <div><Label>Correo envío informe</Label>
                <Input type="email" inputMode="email" autoComplete="email" value={recepcion.correo_envio} onChange={(e) => ur("correo_envio", e.target.value)} className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              <div><Label>Fecha término <Req /></Label>
                <Input type="date" value={recepcion.fecha_termino} onChange={(e) => ur("fecha_termino", e.target.value)} className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              <div className="sm:col-span-2"><Label>Tipo de asistencia <Req /></Label>
                <Select value={recepcion.tipo_asistencia} onValueChange={(v) => ur("tipo_asistencia", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="Seleccionar tipo de asistencia" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-60">{TIPO_ASISTENCIA.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              {recepcion.tipo_asistencia === "otro" && (
                <div className="sm:col-span-2"><Label>Especificar tipo <Req /></Label>
                  <Input value={recepcion.tipo_asistencia_otro || ""} onChange={(e) => ur("tipo_asistencia_otro", e.target.value)} placeholder="Tipo de asistencia..." className="mt-1 h-11 bg-white" disabled={readOnly} /></div>
              )}
            </div>

            {/* Email destinatarios management */}
            <div className="mt-4">
              <h4 className="text-md font-semibold text-[#0FB4B7]">Destinatarios del informe</h4>
              <p className="text-xs text-gray-500 mb-2">El informe se enviará a los siguientes correos. Puedes eliminar los que no desees (excepto reportes@odontofix.cl).</p>
              <div className="space-y-2">
                {/* Fixed: reportes@odontofix.cl */}
                <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 border border-green-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-700">reportes@odontofix.cl</p>
                    <p className="text-[10px] text-green-600">Odontofix (obligatorio)</p>
                  </div>
                  <Badge className="bg-green-100 text-green-700 border-0 text-[10px]">Siempre</Badge>
                </div>
                {/* Correo envío informe */}
                {recepcion.correo_envio && recepcion.correo_envio.includes("@") && (
                  <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2 border border-blue-200">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-700">{recepcion.correo_envio}</p>
                      <p className="text-[10px] text-blue-600">Correo envío informe</p>
                    </div>
                    {!readOnly && <button onClick={() => ur("correo_envio", "")} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>}
                  </div>
                )}
                {/* Dynamic destinatarios */}
                {(recepcion.destinatarios || []).map((d, i) => !d.removido && (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">{d.email}</p>
                      <p className="text-[10px] text-gray-500">{d.origen}</p>
                    </div>
                    {!readOnly && (
                      <button onClick={() => {
                        const dest = [...(recepcion.destinatarios || [])];
                        dest[i] = { ...dest[i], removido: true };
                        ur("destinatarios", dest);
                      }} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mt-4 space-y-2">
              <h4 className="font-semibold text-[#384091]">Resumen del reporte</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {[["Folio", folio || "Pendiente"], ["Cliente", datos.cliente], ["Sucursal", datos.sucursal],
                  ["Equipo", equipoMC.tipo_equipo === "otros" ? equipoMC.tipo_equipo_otro : TIPO_EQUIPO.find((t) => t.value === equipoMC.tipo_equipo)?.label],
                  ["Marca/Modelo", `${datos.marca_sillon} ${datos.modelo}`.trim()], ["Box", datos.numero_box],
                  ["Tipo falla", intervencion.tipo_falla === "otro" ? intervencion.tipo_falla_otro : TIPO_FALLA.find((t) => t.value === intervencion.tipo_falla)?.label],
                  ["Estado final", ESTADO_FINAL.find((e) => e.value === intervencion.estado_final)?.label],
                  ["Verificación", `${verificacion.filter((v) => v.resultado).length}/${verificacion.length} ítems`],
                  ["Fotos", `${intervencion.imagenes.length} imagen(es)`],
                  ["Recibe", recepcion.nombre_recepcion],
                  ["Tipo asistencia", recepcion.tipo_asistencia === "otro" ? recepcion.tipo_asistencia_otro : TIPO_ASISTENCIA.find((t) => t.value === recepcion.tipo_asistencia)?.label],
                ].map(([l, v], i) => v ? <div key={i}><span className="text-gray-500">{l}:</span> <span className="font-medium">{v}</span></div> : null)}
              </div>
              {/* Validation warnings */}
              {!canComplete && (
                <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200 text-xs text-amber-700 space-y-1">
                  <p className="font-medium">Campos pendientes:</p>
                  {!datos.cliente && <p>• Cliente</p>}
                  {!datos.sucursal && <p>• Sucursal</p>}
                  {!equipoMC.tipo_equipo && <p>• Tipo equipo</p>}
                  {!equipoMC.equipo_operativo && <p>• Equipo operativo</p>}
                  {!datos.marca_sillon && <p>• Marca</p>}
                  {!datos.modelo && <p>• Modelo</p>}
                  {!datos.numero_box && <p>• N° Box</p>}
                  {!intervencion.tipo_falla && <p>• Tipo de falla</p>}
                  {!intervencion.diagnostico && <p>• Diagnóstico</p>}
                  {!intervencion.estado_final && <p>• Estado final</p>}
                  {!verifComplete && <p>• Verificación post-reparación ({verificacion.filter((v) => v.resultado).length}/{verificacion.length})</p>}
                  {!hasPhotos && !isAdmin && <p>• Mínimo 1 fotografía</p>}
                  {!recepcion.nombre_recepcion && <p>• Nombre quien recibe</p>}
                  {!recepcion.tipo_asistencia && <p>• Tipo de asistencia</p>}
                  {!recepcion.fecha_termino && <p>• Fecha término</p>}
                </div>
              )}
            </div>

            {folio && !readOnly && <div className="mt-4"><ConsumoRepuestos origenTipo="mc" origenFolio={folio} /></div>}
            {!readOnly && (
              <Button className="w-full bg-[#0FB4B7] hover:bg-[#0D999C] text-white mt-4" size="lg"
                onClick={completeReport} disabled={completing || !canComplete}>
                {completing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {estado === "completado" ? "Regenerar y Reenviar Reporte" : "Completar y generar PDF"}
              </Button>
            )}
          </CardContent></Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
            <ArrowLeft className="h-4 w-4 mr-1" />Anterior
          </Button>
          {step < STEPS.length - 1 && (
            <Button onClick={() => setStep(step + 1)} className="bg-[#384091] hover:bg-[#2D3375] text-white">
              Siguiente<ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
